// Campus visual configuration — maps club state to tiered facility
// illustrations (PC Futbol stadium-section style). Asset filenames are
// the contract with docs/ASSET_PROMPTS.md: the game renders elegant
// placeholders until the webp files exist in public/assets/campus/.

import type { Court } from '@/store/gameStore'
import type { Building } from '@/config/buildingConfig'
import type { RegionId } from '@/config/regionConfig'

/** 'court' or any facility id from FACILITY_REGISTRY (open set on purpose) */
export type FacilityId = string

/** Minimal snapshot of game state needed to derive campus visuals */
export interface CampusSnapshot {
  clubLevel: number
  courts: Court[]
  buildings: Building[]
  courtUpgrades: Record<string, string[]>
}

export interface FacilityTierAsset {
  tier: number
  image: string
  caption: string
}

export interface FacilityStatus {
  label: string
  tone: 'ok' | 'warn' | 'danger'
}

export interface FacilityVisual {
  id: FacilityId
  name: string
  /** Two gradient stops for the placeholder while assets are missing */
  placeholderGradient: [string, string]
  tiers: FacilityTierAsset[]
  /** Deep-link to the management tab (anti-FM26: never bury actions) */
  manageRoute: string
  /** Derive current tier from state; -1 = not built yet */
  getTier: (s: CampusSnapshot) => number
  getStatus?: (s: CampusSnapshot) => FacilityStatus | null
}

export interface ClubHeroAsset {
  level: number
  image: string
  name: string
}

export const CLUB_HERO_ASSETS: ClubHeroAsset[] = [
  { level: 0, image: '/assets/campus/hero/club_l0.webp', name: 'Terreno Baldio' },
  { level: 1, image: '/assets/campus/hero/club_l1.webp', name: 'Pista de Barrio' },
  { level: 2, image: '/assets/campus/hero/club_l2.webp', name: 'Club Incipiente' },
  { level: 3, image: '/assets/campus/hero/club_l3.webp', name: 'Club Establecido' },
  { level: 4, image: '/assets/campus/hero/club_l4.webp', name: 'Club Reconocido' },
  { level: 5, image: '/assets/campus/hero/club_l5.webp', name: 'Club de Referencia' },
  { level: 6, image: '/assets/campus/hero/club_l6.webp', name: 'Centro Deportivo' },
  { level: 7, image: '/assets/campus/hero/club_l7.webp', name: 'Club de Elite' },
]

export function getClubHero(clubLevel: number): ClubHeroAsset {
  const clamped = Math.max(0, Math.min(CLUB_HERO_ASSETS.length - 1, clubLevel))
  return CLUB_HERO_ASSETS[clamped]
}

// --- Regional asset variants -------------------------------------------------
// Convention: the NEUTRAL files ARE Costa del Sol (the identity region, already
// Mediterranean golden-hour). Regional variants insert the region id as a
// folder: /assets/campus/hero/norte_lluvioso/club_l3.webp. A missing regional
// file costs one 404 and falls back to the neutral art (AssetImage chain), so
// art can land lot by lot without ever breaking the UI.

/** Ordered candidate URLs for an asset: regional variant first, neutral second. */
export function regionalAssetCandidates(image: string, region: RegionId): string[] {
  if (region === 'costa_sol') return [image]
  const i = image.lastIndexOf('/')
  return [`${image.slice(0, i)}/${region}${image.slice(i)}`, image]
}

/** Facilities with region-specific art. Interiors (reception, shop, locker,
 *  cafe...) are deliberately region-neutral: only what reads as EXTERIOR gets
 *  variants, which keeps the art matrix small. */
export const REGIONAL_FACILITY_IDS: ReadonlySet<string> = new Set(['court'])

/** Candidate URLs for a facility tier image (regional only for exteriors). */
export function facilityAssetCandidates(
  visual: FacilityVisual,
  asset: FacilityTierAsset,
  region: RegionId,
): string[] {
  return REGIONAL_FACILITY_IDS.has(visual.id)
    ? regionalAssetCandidates(asset.image, region)
    : [asset.image]
}

/** Building level (1-3) or -1 if not built */
function buildingTier(s: CampusSnapshot, type: Building['type']): number {
  const building = s.buildings.find(b => b.type === type)
  return building ? (building.level ?? 1) : -1
}

function avgCourtCondition(s: CampusSnapshot): number {
  if (s.courts.length === 0) return 100
  return s.courts.reduce((sum, c) => sum + c.condition, 0) / s.courts.length
}

/** Average court condition as a hero-level alert (ok tone = nothing to show) */
export function conditionStatus(s: CampusSnapshot): FacilityStatus | null {
  if (s.courts.length === 0) return null
  const avg = avgCourtCondition(s)
  if (avg < 30) return { label: 'Estado critico', tone: 'danger' }
  if (avg < 60) return { label: 'Necesita mantenimiento', tone: 'warn' }
  return { label: 'En buen estado', tone: 'ok' }
}

export const FACILITY_VISUALS: FacilityVisual[] = [
  {
    id: 'court',
    name: 'Pistas',
    placeholderGradient: ['#1E3A5F', '#38BDF8'],
    tiers: [
      { tier: 0, image: '/assets/campus/court/court_t0.webp', caption: 'Pista basica descubierta' },
      { tier: 1, image: '/assets/campus/court/court_t1.webp', caption: 'Iluminacion LED nocturna' },
      { tier: 2, image: '/assets/campus/court/court_t2.webp', caption: 'Superficie premium azul' },
      { tier: 3, image: '/assets/campus/court/court_t3.webp', caption: 'Pista indoor cubierta' },
    ],
    manageRoute: '/facilities?tab=pistas',
    // Showcase the best court the club owns
    getTier: (s) => {
      if (s.courts.length === 0) return -1
      if (s.courts.some(c => c.type === 'indoor')) return 3
      const upgrades = Object.values(s.courtUpgrades).flat()
      if (upgrades.includes('premium_surface')) return 2
      if (upgrades.includes('led_lighting')) return 1
      return 0
    },
    getStatus: conditionStatus,
  },
  {
    id: 'reception',
    name: 'Recepcion',
    placeholderGradient: ['#1E3A5F', '#4ADE80'],
    tiers: [
      { tier: 1, image: '/assets/campus/reception/reception_t1.webp', caption: 'Mostrador sencillo' },
      { tier: 2, image: '/assets/campus/reception/reception_t2.webp', caption: 'Recepcion moderna' },
      { tier: 3, image: '/assets/campus/reception/reception_t3.webp', caption: 'Lobby premium' },
    ],
    manageRoute: '/facilities?tab=edificios',
    getTier: (s) => buildingTier(s, 'reception'),
  },
  {
    id: 'shop',
    name: 'Pro Shop',
    placeholderGradient: ['#1E3A5F', '#FB923C'],
    tiers: [
      { tier: 1, image: '/assets/campus/shop/shop_t1.webp', caption: 'Estanteria de palas' },
      { tier: 2, image: '/assets/campus/shop/shop_t2.webp', caption: 'Tienda equipada' },
      { tier: 3, image: '/assets/campus/shop/shop_t3.webp', caption: 'Boutique deportiva' },
    ],
    manageRoute: '/facilities?tab=edificios',
    getTier: (s) => buildingTier(s, 'shop'),
  },
  {
    id: 'locker',
    name: 'Vestuarios',
    placeholderGradient: ['#1E3A5F', '#94A3B8'],
    tiers: [
      { tier: 1, image: '/assets/campus/locker/locker_t1.webp', caption: 'Vestuarios funcionales' },
      { tier: 2, image: '/assets/campus/locker/locker_t2.webp', caption: 'Vestuarios con spa' },
    ],
    manageRoute: '/facilities?tab=edificios',
    getTier: (s) => buildingTier(s, 'locker'),
  },
  {
    id: 'cafe',
    name: 'Cafeteria',
    placeholderGradient: ['#1E3A5F', '#F87171'],
    tiers: [
      { tier: 1, image: '/assets/campus/cafe/cafe_t1.webp', caption: 'Barra de cafe' },
      { tier: 2, image: '/assets/campus/cafe/cafe_t2.webp', caption: 'Cafeteria con terraza' },
      { tier: 3, image: '/assets/campus/cafe/cafe_t3.webp', caption: 'Restaurante del club' },
    ],
    manageRoute: '/facilities?tab=edificios',
    getTier: (s) => buildingTier(s, 'cafe'),
  },
  {
    id: 'parking',
    name: 'Parking',
    placeholderGradient: ['#1E3A5F', '#38BDF8'],
    tiers: [
      { tier: 1, image: '/assets/campus/parking/parking_t1.webp', caption: 'Parking del club' },
    ],
    manageRoute: '/facilities?tab=edificios',
    getTier: (s) => buildingTier(s, 'parking'),
  },
  {
    id: 'pool',
    name: 'Piscina',
    placeholderGradient: ['#0E7490', '#38BDF8'],
    tiers: [
      { tier: 1, image: '/assets/campus/pool/pool_t1.webp', caption: 'Piscina del club' },
    ],
    manageRoute: '/facilities?tab=edificios',
    getTier: (s) => buildingTier(s, 'pool'),
  },
  {
    id: 'gym',
    name: 'Gimnasio',
    placeholderGradient: ['#1E3A5F', '#A78BFA'],
    tiers: [
      { tier: 1, image: '/assets/campus/gym/gym_t1.webp', caption: 'Gimnasio del club' },
    ],
    manageRoute: '/facilities?tab=edificios',
    getTier: (s) => buildingTier(s, 'gym'),
  },
  {
    id: 'academy',
    name: 'Academia',
    placeholderGradient: ['#1E3A5F', '#4ADE80'],
    tiers: [
      { tier: 1, image: '/assets/campus/academy/academy_t1.webp', caption: 'Academia de padel' },
    ],
    manageRoute: '/facilities?tab=edificios',
    getTier: (s) => buildingTier(s, 'academy'),
  },
  {
    id: 'kids_zone',
    name: 'Zona Infantil',
    placeholderGradient: ['#1E3A5F', '#FB923C'],
    tiers: [
      { tier: 1, image: '/assets/campus/kids_zone/kids_zone_t1.webp', caption: 'Zona infantil' },
    ],
    manageRoute: '/facilities?tab=edificios',
    getTier: (s) => buildingTier(s, 'kids_zone'),
  },
  {
    id: 'vip_box',
    name: 'Palco VIP',
    placeholderGradient: ['#1E3A5F', '#F5C542'],
    tiers: [
      { tier: 1, image: '/assets/campus/vip_box/vip_box_t1.webp', caption: 'Palco VIP' },
    ],
    manageRoute: '/facilities?tab=edificios',
    getTier: (s) => buildingTier(s, 'vip_box'),
  },
  {
    id: 'press_room',
    name: 'Sala de Prensa',
    placeholderGradient: ['#1E3A5F', '#94A3B8'],
    tiers: [
      { tier: 1, image: '/assets/campus/press_room/press_room_t1.webp', caption: 'Sala de prensa' },
    ],
    manageRoute: '/facilities?tab=edificios',
    getTier: (s) => buildingTier(s, 'press_room'),
  },
  {
    id: 'solar',
    name: 'Placas Solares',
    placeholderGradient: ['#1E3A5F', '#FBBF24'],
    tiers: [
      { tier: 1, image: '/assets/campus/solar/solar_t1.webp', caption: 'Placas solares' },
    ],
    manageRoute: '/facilities?tab=edificios',
    getTier: (s) => buildingTier(s, 'solar'),
  },
]

export function getFacilityAsset(visual: FacilityVisual, tier: number): FacilityTierAsset | null {
  if (tier < 0) return null
  // Closest defined tier at or below the derived one
  const sorted = [...visual.tiers].sort((a, b) => b.tier - a.tier)
  return sorted.find(t => t.tier <= tier) ?? visual.tiers[0] ?? null
}

// === Daily campus actions (Top Eleven pattern: facilities anchor
// daily gameplay, not just cosmetics) ===

export interface DailyActionDefinition {
  id: string
  facilityId: FacilityId
  name: string
  description: string
  cost: number
  cooldownDays: number
}

export const DAILY_ACTIONS: DailyActionDefinition[] = [
  {
    id: 'court_tuneup',
    facilityId: 'court',
    name: 'Puesta a punto',
    description: '+10 condicion a la pista mas desgastada',
    cost: 50,
    cooldownDays: 1,
  },
  {
    id: 'cafe_promo',
    facilityId: 'cafe',
    name: 'Promo del dia',
    description: '+10% ingresos del club durante 1 dia',
    cost: 100,
    cooldownDays: 1,
  },
  {
    id: 'member_newsletter',
    facilityId: 'reception',
    name: 'Boletin a socios',
    description: '+0.02 reputacion',
    cost: 0,
    cooldownDays: 3,
  },
]
